import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiCheckCircle, FiArrowRight, FiTool, FiClipboard,
  FiTruck, FiSettings, FiHeadphones
} from 'react-icons/fi';
import { 
  FaSolarPanel, FaChartLine, FaHandshake,
  FaGraduationCap, FaCogs
} from 'react-icons/fa';
import { useModal } from '../../context/ModalContext';

const Services = () => {
  const { openModal } = useModal();
  const services = [
    {
      icon: <FiClipboard className="text-3xl" />,
      title: 'Consultation & Site Survey',
      description: 'Comprehensive site assessment and expert consultation to determine the best solar solution for your needs.',
      features: [
        'Free site visit and assessment',
        'Energy consumption analysis',
        'Shadow analysis and optimization',
        'Customized system design',
        'ROI calculation and proposal',
      ],
    },
    {
      icon: <FaSolarPanel className="text-3xl" />,
      title: 'Solar System Design',
      description: 'Professional solar system design by experienced engineers for maximum efficiency and reliability.',
      features: [
        'Custom system architecture',
        'Load calculation and sizing',
        'Component selection',
        '3D design visualization',
        'Electrical layout planning',
      ],
    },
    {
      icon: <FiTruck className="text-3xl" />,
      title: 'Procurement & Delivery',
      description: 'Sourcing and delivery of high-quality solar equipment from certified manufacturers.',
      features: [
        'ALMM listed solar panels',
        'BIS certified components',
        'Quality inspection',
        'Timely delivery',
        'Proper handling and storage',
      ],
    },
    {
      icon: <FiTool className="text-3xl" />,
      title: 'Installation & Commissioning',
      description: 'Professional installation by trained technicians following industry best practices and safety standards.',
      features: [
        'Expert installation team',
        'Quality workmanship',
        'Safety compliance',
        'System testing',
        'Grid synchronization',
      ],
    },
    {
      icon: <FiSettings className="text-3xl" />,
      title: 'Operations & Maintenance',
      description: 'Comprehensive O&M services to ensure optimal performance and longevity of your solar system.',
      features: [
        'Regular system monitoring',
        'Preventive maintenance',
        'Panel cleaning services',
        'Performance optimization',
        'Emergency repairs',
      ],
    },
    {
      icon: <FiHeadphones className="text-3xl" />,
      title: 'After-Sales Support',
      description: '24/7 customer support and warranty services for complete peace of mind.',
      features: [
        'Dedicated support team',
        'Warranty management',
        'Spare parts availability',
        'Remote monitoring',
        'Performance reports',
      ],
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Inquiry',
      description: 'Contact us with your requirements through call, email, or website form.',
    },
    {
      step: '02',
      title: 'Site Survey',
      description: 'Our team visits your location for detailed assessment and measurements.',
    },
    {
      step: '03',
      title: 'Proposal',
      description: 'Receive a customized proposal with system design, pricing, and ROI analysis.',
    },
    {
      step: '04',
      title: 'Agreement',
      description: 'Sign the agreement and complete documentation including subsidies.',
    },
    {
      step: '05',
      title: 'Installation',
      description: 'Professional installation by our certified technicians.',
    },
    {
      step: '06',
      title: 'Commissioning',
      description: 'System testing, grid connection, and handover with warranty activation.',
    },
  ];

  const specialServices = [
    {
      icon: <FaChartLine className="text-3xl" />,
      title: 'Subsidy Assistance',
      description: 'Complete assistance with PM KUSUM, state subsidies, and net metering applications.',
    },
    {
      icon: <FaHandshake className="text-3xl" />,
      title: 'Financing Solutions',
      description: 'Flexible financing options including EMI, bank loans, and OPEX models.',
    },
    {
      icon: <FaGraduationCap className="text-3xl" />,
      title: 'Training & Education',
      description: 'Training programs for farmers and operators on system operation and maintenance.',
    },
    {
      icon: <FaCogs className="text-3xl" />,
      title: 'System Upgrades',
      description: 'Upgrade existing solar installations with latest technology and components.',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[280px] md:h-[340px] flex items-center justify-center overflow-hidden mt-20 md:mt-24">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920')] bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-dark-900/90 via-dark-800/80 to-dark-900/70" />
        <div className="container relative z-10 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4 uppercase tracking-wide">Our Services</h1>
            <p className="text-base md:text-xl text-white/90 mb-4">End-to-end solar solutions from consultation to maintenance</p>
            <div className="flex items-center justify-center gap-2 text-sm text-white/80">
              <Link to="/" className="hover:text-solar-400 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-solar-400">Services</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container">
          <motion.div 
            className="text-center mb-8 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">What We Offer</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Comprehensive Solar Services</h2>
            <p className="text-dark-500 max-w-2xl mx-auto">
              From initial consultation to ongoing maintenance, we provide complete 
              solar solutions tailored to your needs.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group border-b-4 border-transparent hover:border-primary-500 rounded-2xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-solar-500 text-white flex items-center justify-center mb-6 group-hover:scale-110 transition-transform rounded-xl">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-dark-800 mb-3">{service.title}</h3>
                <p className="text-dark-500 mb-5 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-dark-600">
                      <FiCheckCircle className="text-secondary-500" /> {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container">
          <motion.div 
            className="text-center mb-8 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">How It Works</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Our Simple Process</h2>
            <p className="text-dark-500 max-w-2xl mx-auto">
              We make going solar easy with our streamlined 6-step process.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-300 relative overflow-hidden group rounded-2xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-7xl font-extrabold text-primary-100 absolute -top-4 -right-2 group-hover:text-primary-200 transition-colors">{step.step}</div>
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-xl font-bold flex items-center justify-center mx-auto mb-4 rounded-lg">
                    {step.step}
                  </div>
                  <h4 className="text-lg font-bold text-dark-800 mb-2">{step.title}</h4>
                  <p className="text-dark-500 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Services */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container">
          <motion.div 
            className="text-center mb-8 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">Additional Services</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Value-Added Services</h2>
            <p className="text-dark-500 max-w-2xl mx-auto">
              Beyond installation, we offer comprehensive support services to maximize your solar investment.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {specialServices.map((service, index) => (
              <motion.div
                key={index}
                className="flex items-center gap-6 bg-gray-50 p-6 shadow-md hover:shadow-xl transition-all duration-300 group rounded-xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-secondary-500 to-secondary-600 text-white flex items-center justify-center group-hover:scale-110 transition-transform rounded-xl">
                  {service.icon}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-dark-800 mb-1">{service.title}</h4>
                  <p className="text-dark-500 text-sm">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">Coverage</span>
              <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4 md:mb-6 leading-tight">Pan-India Service Network</h2>
              <p className="text-dark-500 leading-relaxed mb-8">
                With a strong presence across 15+ states, we provide reliable solar services 
                to customers throughout India. Our extensive network ensures quick response 
                times and local support.
              </p>
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                <div>
                  <h4 className="font-bold text-dark-800 mb-2 border-l-4 border-primary-500 pl-3">North India</h4>
                  <ul className="space-y-1 text-dark-500 text-sm ml-4">
                    <li>Rajasthan</li>
                    <li>Gujarat</li>
                    <li>Punjab</li>
                    <li>Haryana</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-dark-800 mb-2 border-l-4 border-primary-500 pl-3">West India</h4>
                  <ul className="space-y-1 text-dark-500 text-sm ml-4">
                    <li>Maharashtra</li>
                    <li>Madhya Pradesh</li>
                    <li>Goa</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-dark-800 mb-2 border-l-4 border-secondary-500 pl-3">South India</h4>
                  <ul className="space-y-1 text-dark-500 text-sm ml-4">
                    <li>Karnataka</li>
                    <li>Tamil Nadu</li>
                    <li>Andhra Pradesh</li>
                    <li>Telangana</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-dark-800 mb-2 border-l-4 border-secondary-500 pl-3">East India</h4>
                  <ul className="space-y-1 text-dark-500 text-sm ml-4">
                    <li>Jharkhand</li>
                    <li>Bihar</li>
                    <li>Odisha</li>
                    <li>West Bengal</li>
                  </ul>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=600" 
                alt="Service Areas"
                className="w-full h-[300px] md:h-[500px] object-cover shadow-2xl rounded-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-base md:text-xl text-white/85 mb-6 md:mb-8">Contact us today for a free consultation and site survey.</p>
            <button onClick={openModal} className="btn btn-secondary">
              Schedule Free Consultation <FiArrowRight className="inline ml-2" />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
